import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { Bell, Plus, List, Gavel, LogOut, User, Home, Handshake, Check, Menu, X, LogIn, MessageCircle, HelpCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const notificationRef = useRef(null);

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const markAsRead = async (notifId) => {
    try {
      await axios.patch(`${API_URL}/api/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
    } catch (error) {
      console.error('Failed to mark notification as read', error);
    }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.read) await markAsRead(notif.id);
    setShowNotifications(false);
    switch (notif.type) {
      case 'bid_received':
        navigate('/my-listings');
        break;
      case 'bid_accepted':
      case 'bid_rejected':
      case 'bid_countered':
        navigate('/my-bids');
        break;
      case 'message':
        if (notif.related_id) {
          navigate(`/chat/${notif.related_id}`);
        } else {
          navigate('/messages');
        }
        break;
      case 'trade_initiated':
      case 'trade_created':
      case 'trade_confirmed':
      case 'trade_confirmed_partial':
      case 'trade_completed':
        navigate('/trades');
        break;
      case 'review_received':
        navigate(`/profile/${user?.id}`);
        break;
      case 'listing_removed':
        navigate('/my-listings');
        break;
      default:
        navigate('/');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.patch(`${API_URL}/api/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'bid_received': '🔔', 'bid_accepted': '✅', 'bid_rejected': '❌',
      'bid_countered': '💬', 'message': '✉️',
      'trade_initiated': '🤝', 'trade_created': '🤝', 
      'trade_confirmed': '✨', 'trade_confirmed_partial': '👍',
      'trade_completed': '🎉',
      'review_received': '⭐', 'listing_removed': '🚫'
    };
    return icons[type] || '📢';
  };

  const formatTimeAgo = (dateString) => {
    const diffMs = new Date() - new Date(dateString);
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return new Date(dateString).toLocaleDateString();
  };

  // Navigation links for logged-in users
  const authNavLinks = [
    { to: '/', icon: Home, label: 'Home', color: '#4F46E5' },
    { to: '/create-listing', icon: Plus, label: 'Create', color: '#10B981' },
    { to: '/my-bids', icon: Gavel, label: 'My Bids', color: '#F59E0B' },
    { to: '/my-listings', icon: List, label: 'Listings', color: '#8B5CF6' },
    { to: '/messages', icon: MessageCircle, label: 'Messages', color: '#06B6D4' },
    { to: '/trades', icon: Handshake, label: 'Trades', color: '#EC4899' },
    { to: '/faq', icon: HelpCircle, label: 'FAQs', color: '#6366F1' },
  ];

  // Navigation links for guests
  const guestNavLinks = [
    { to: '/', icon: Home, label: 'Home', color: '#4F46E5' },
    { to: '/faq', icon: HelpCircle, label: 'FAQs', color: '#6366F1' },
  ];

  const navLinks = user ? authNavLinks : guestNavLinks;
  const isActive = (path) => location.pathname === path;

  // 3D Icon component for navbar
  const NavIcon3D = ({ Icon, color, isActive }) => (
    <div 
      className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
      style={{ 
        background: isActive ? color : `${color}15`,
        boxShadow: isActive 
          ? `0 4px 8px -2px ${color}40, inset 0 1px 2px rgba(255,255,255,0.3)` 
          : `0 2px 4px -1px ${color}20`,
      }}
    >
      <Icon 
        className="w-4 h-4 transition-colors" 
        style={{ color: isActive ? 'white' : color }}
        strokeWidth={2} 
      />
    </div>
  );

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Main navbar row */}
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img src="/logo.png" alt="B2B" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation - 3D Icons */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`flex flex-col items-center px-3 py-1.5 rounded-xl text-xs font-medium transition-all hover:scale-105 ${
                  isActive(link.to)
                    ? 'bg-slate-50'
                    : 'hover:bg-slate-50'
                }`}
              >
                <NavIcon3D Icon={link.icon} color={link.color} isActive={isActive(link.to)} />
                <span className={`mt-1 ${isActive(link.to) ? 'text-slate-900 font-semibold' : 'text-slate-600'}`}>
                  {link.label}
                </span>
              </Link>
            ))}
          </div>

          {/* Desktop Right Section */}
          <div className="hidden md:flex items-center space-x-2">
            {user ? (
              <>
                {/* Notifications */}
                <div className="relative" ref={notificationRef}>
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
                      <div className="p-3 border-b bg-slate-50 flex items-center justify-between">
                        <span className="font-semibold text-slate-900 text-sm">Notifications</span>
                        {unreadCount > 0 && (
                          <button onClick={markAllAsRead} className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Mark all read
                          </button>
                        )}
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="p-6 text-center text-slate-500">
                            <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-sm">No notifications</p>
                          </div>
                        ) : (
                          notifications.slice(0, 10).map((notif) => (
                            <div
                              key={notif.id}
                              onClick={() => handleNotificationClick(notif)}
                              className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${!notif.read ? 'bg-indigo-50/50' : ''}`}
                            >
                              <div className="flex items-start gap-2">
                                <span className="text-lg">{getNotificationIcon(notif.type)}</span>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${!notif.read ? 'font-semibold' : ''} text-slate-900`}>{notif.title}</p>
                                  <p className="text-xs text-slate-500 truncate">{notif.message}</p>
                                  <p className="text-xs text-slate-400 mt-1">{formatTimeAgo(notif.created_at)}</p>
                                </div>
                                {!notif.read && <span className="w-2 h-2 bg-indigo-600 rounded-full mt-1.5"></span>}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* User */}
                <Link to={`/profile/${user?.id}`} className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium max-w-[100px] truncate">{user?.name}</span>
                </Link>

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                {/* Login Button for Guests */}
                <Link
                  to="/auth"
                  className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Login / Register</span>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Right Section */}
          <div className="flex md:hidden items-center space-x-1">
            {user && (
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 text-slate-600 rounded-lg"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-700 rounded-lg hover:bg-slate-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-100 py-2 bg-white">
            {user ? (
              <>
                {/* Navigation Links for Logged-in User - 3D Icons */}
                <div className="grid grid-cols-4 gap-2 px-3 py-3">
                  {authNavLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`flex flex-col items-center justify-center py-2 px-1 rounded-xl text-center transition-all ${
                        isActive(link.to)
                          ? 'bg-slate-50'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <NavIcon3D Icon={link.icon} color={link.color} isActive={isActive(link.to)} />
                      <span className={`text-[10px] mt-1 font-medium leading-tight ${isActive(link.to) ? 'text-slate-900' : 'text-slate-600'}`}>
                        {link.label}
                      </span>
                    </Link>
                  ))}
                </div>

                <div className="border-t border-slate-100 my-2"></div>

                {/* User Section */}
                <div className="px-3 py-2">
                  <Link
                    to={`/profile/${user?.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50"
                  >
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                  </Link>
                </div>

                {/* Logout Button */}
                <div className="px-3 py-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 p-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Logout</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Guest Mobile Menu - 3D Icons */}
                <div className="px-3 py-4">
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {guestNavLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className={`flex flex-col items-center justify-center py-3 px-2 rounded-xl text-center transition-all ${
                          isActive(link.to)
                            ? 'bg-slate-50'
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <NavIcon3D Icon={link.icon} color={link.color} isActive={isActive(link.to)} />
                        <span className={`text-xs mt-1 font-medium ${isActive(link.to) ? 'text-slate-900' : 'text-slate-600'}`}>
                          {link.label}
                        </span>
                      </Link>
                    ))}
                  </div>
                  
                  <div className="text-center mb-4">
                    <h3 className="font-semibold text-slate-900 mb-1">Welcome to Bid2Barter!</h3>
                    <p className="text-sm text-slate-500">Login to start trading</p>
                  </div>
                  
                  <Link
                    to="/auth"
                    className="w-full flex items-center justify-center gap-2 p-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors"
                  >
                    <LogIn className="w-5 h-5" />
                    <span>Login / Register</span>
                  </Link>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
